import { memo, useMemo } from "react";

import { QuestionConnection as Question } from "@/components/features/items/edit/_shared/ui/components/main/questions/connection";
import { AccordionOnlyUseBasic } from "@/components/features/items/edit/_shared/ui/components/system/accordion/only-use-basic/accordion";

import { Image } from "@/components/ui/image/image";

import { usePlanRequiredOptionIDs } from "./required-option-connection/hooks";
import { usePlanRequiredOptionGroupIDs } from "./required-option-group-connection/hooks";

export const QuestionPlanRequiredOptionCombinedConnection = memo(() => {
	const {
		availableItemIDs: eachAvailableItemIDs,
		rawAvailableItemIDs: eachRawAvailableItemIDs,
		nameAndIDOptions: eachNameAndIDOptions,
		onSetAvailableItemIDs: onSetEachAvailableItemIDs,
		hotelID,
		isEdited: eachIsEdited,
	} = usePlanRequiredOptionIDs();

	const {
		availableItemIDs: anyAvailableItemIDs,
		rawAvailableItemIDs: anyRawAvailableItemIDs,
		nameAndIDOptions: anyNameAndIDOptions,
		onSetAvailableItemIDs: onSetAnyAvailableItemIDs,
		isEdited: anyIsEdited,
	} = usePlanRequiredOptionGroupIDs();

	// 対となる設定と同じオプションを両方に設定できないよう、もう一方で選択中の項目を選択不可にする
	// （両方に選択済みの既存データは、解除できるように選択可能のまま残す）
	const eachNameAndIDOptionsWithDisabled = useMemo(
		() =>
			eachNameAndIDOptions.map((item) => ({
				...item,
				disabled: anyRawAvailableItemIDs.includes(item.value) && !eachRawAvailableItemIDs.includes(item.value),
			})),
		[eachNameAndIDOptions, anyRawAvailableItemIDs, eachRawAvailableItemIDs],
	);
	const anyNameAndIDOptionsWithDisabled = useMemo(
		() =>
			anyNameAndIDOptions.map((item) => ({
				...item,
				disabled: eachRawAvailableItemIDs.includes(item.value) && !anyRawAvailableItemIDs.includes(item.value),
			})),
		[anyNameAndIDOptions, eachRawAvailableItemIDs, anyRawAvailableItemIDs],
	);

	return (
		<AccordionOnlyUseBasic
			hotelID={hotelID}
			defaultOpen={eachIsEdited || anyIsEdited}
			accordionTitle="オプションの必須選択を設定する"
		>
			<div className="overflow-hidden rounded-lg border border-zinc-200">
				<Image
					src="/assets/option/required_options.png"
					alt="必須オプションの設定について（パターン1: すべてのオプションの選択を必須とする設定 / パターン2: いずれか1つだけの選択を必須とする設定）"
					className="w-full"
				/>
			</div>
			<div className="space-y-5">
				<Question
					title="すべてのオプションの選択を必須とする設定"
					description="※ ここで選択したオプションは、ゲストがすべて選択する必要があります（時間貸し・通常オプションどちらも対象）。選択必須なオプションの在庫が無い場合、プランは販売されません。「いずれか1つだけの選択を必須とする設定」で選択中のオプションは選択できません。"
					availableItemIDs={eachAvailableItemIDs}
					rawAvailableItemIDs={eachRawAvailableItemIDs}
					nameAndIDOptions={eachNameAndIDOptionsWithDisabled}
					onSetAvailableItemIDs={onSetEachAvailableItemIDs}
				/>
			</div>
			<div className="space-y-5">
				<Question
					title="いずれか1つだけの選択を必須とする設定"
					description="※ ここで選択したオプションのうち、ゲストはいずれか1つを選択する必要があります。複数選択はできません。「すべてのオプションの選択を必須とする設定」で選択中のオプションは選択できません。"
					availableItemIDs={anyAvailableItemIDs}
					rawAvailableItemIDs={anyRawAvailableItemIDs}
					nameAndIDOptions={anyNameAndIDOptionsWithDisabled}
					onSetAvailableItemIDs={onSetAnyAvailableItemIDs}
				/>
			</div>
		</AccordionOnlyUseBasic>
	);
});

QuestionPlanRequiredOptionCombinedConnection.displayName = "QuestionPlanRequiredOptionCombinedConnection";
